import { useEffect, useRef } from 'react';

interface CalendlyWidgetProps {
  url: string;
  height?: number;
  onScheduled?: () => void;
}

interface CalendlyApi {
  initInlineWidget: (options: {
    url: string;
    parentElement: HTMLElement;
    prefill?: Record<string, unknown>;
    utm?: Record<string, unknown>;
  }) => void;
}

declare global {
  interface Window {
    Calendly?: CalendlyApi;
  }
}

const WIDGET_SCRIPT_URL = 'https://assets.calendly.com/assets/external/widget.js';

export function CalendlyWidget({ url, height = 700, onScheduled }: CalendlyWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);
  const onScheduledRef = useRef(onScheduled);

  useEffect(() => {
    onScheduledRef.current = onScheduled;
  }, [onScheduled]);

  useEffect(() => {
    let cancelled = false;

    const init = () => {
      if (cancelled || initializedRef.current || !containerRef.current || !window.Calendly) {
        return;
      }
      initializedRef.current = true;
      window.Calendly.initInlineWidget({
        url,
        parentElement: containerRef.current,
      });
    };

    const existing = document.querySelector(
      `script[src="${WIDGET_SCRIPT_URL}"]`,
    ) as HTMLScriptElement | null;

    if (existing) {
      if (window.Calendly) {
        init();
      } else {
        existing.addEventListener('load', init);
      }
    } else {
      const script = document.createElement('script');
      script.src = WIDGET_SCRIPT_URL;
      script.async = true;
      script.onload = init;
      document.head.appendChild(script);
    }

    return () => {
      cancelled = true;
      initializedRef.current = false;
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [url]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const data = event.data;
      if (data && typeof data === 'object' && data.event === 'calendly.event_scheduled') {
        onScheduledRef.current?.();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <div
      ref={containerRef}
      className="w-full"
      style={{ minWidth: '320px', height: `${height}px` }}
    />
  );
}

export default CalendlyWidget;